import { storage } from "@/utils/storage";
import { getApiOriginFromBase } from "@/lib/apiUrl";
import {
  getApiUrl,
  getPgTileserverUrl,
  getTileserverUrl,
} from "@/config/runtime";

const AUTH_TOKEN_STORAGE_KEY = "auth_token";
const USER_INFO_STORAGE_KEY = "user_info";
export const WEB_AUTH_SESSION_STORAGE_KEY = "web_auth_session";
export const WEB_AUTH_SESSION_EVENT_STORAGE_KEY = "web_auth_session_event";
const CSRF_TOKEN_STORAGE_KEY = "csrf_token";
const DEFAULT_CLIENT_ORIGIN = "http://localhost";
const TILES_PATH_PREFIXES = ["/tiles/", "/pg-tiles/"];

export const getClientOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_CLIENT_ORIGIN;
};

export const getAuthToken = (): string | null => {
  try {
    return storage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const getCsrfToken = (): string | null => {
  try {
    return storage.getItem(CSRF_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setCsrfToken = (token?: unknown): void => {
  if (typeof token !== "string") return;

  const trimmedToken = token.trim();
  if (!trimmedToken) return;

  storage.setItem(CSRF_TOKEN_STORAGE_KEY, trimmedToken);
};

export const markWebAuthSession = (csrfToken?: unknown): void => {
  storage.setItem(WEB_AUTH_SESSION_STORAGE_KEY, "1");
  setCsrfToken(csrfToken);
};

export const hasWebAuthSession = (): boolean => {
  try {
    return storage.getItem(WEB_AUTH_SESSION_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const hasAuthSession = (): boolean =>
  Boolean(getAuthToken() || hasWebAuthSession());

export const shouldRestoreWebAuthSession = (): boolean =>
  !getAuthToken() && hasWebAuthSession();

export type WebAuthSessionChange = "signed-in" | "signed-out";

export const announceWebAuthSessionChange = (
  change: WebAuthSessionChange
): void => {
  const nonce =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  storage.setItem(WEB_AUTH_SESSION_EVENT_STORAGE_KEY, `${change}:${nonce}`);
};

export const getWebAuthSessionChange = (
  key: string | null,
  newValue: string | null
): WebAuthSessionChange | null => {
  if (key === WEB_AUTH_SESSION_STORAGE_KEY && newValue === null) {
    return "signed-out";
  }
  if (key !== WEB_AUTH_SESSION_EVENT_STORAGE_KEY || !newValue) return null;
  if (newValue.startsWith("signed-in:")) return "signed-in";
  if (newValue.startsWith("signed-out:")) return "signed-out";
  return null;
};

export const clearCachedAuthIdentity = (): void => {
  storage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  storage.removeItem(USER_INFO_STORAGE_KEY);
};

export const hasResolvedAuthSession = (
  hasCheckedCookieSession: boolean
): boolean =>
  Boolean(getAuthToken() || (hasCheckedCookieSession && hasWebAuthSession()));

export const clearAuthSession = (): void => {
  storage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  storage.removeItem(WEB_AUTH_SESSION_STORAGE_KEY);
  storage.removeItem(CSRF_TOKEN_STORAGE_KEY);
};

/**
 * True when `rawUrl` is a `/tiles/` or `/pg-tiles/` URL served by one of our
 * own backends (page origin, configured API, or configured tile servers), so
 * it is safe to attach the session token to the request.
 */
export const isFirstPartyTilesUrl = (rawUrl?: string) => {
  if (!rawUrl) return false;

  const fallbackOrigin = getClientOrigin();
  try {
    const resolved = new URL(rawUrl, fallbackOrigin);
    const hasTilesPrefix = TILES_PATH_PREFIXES.some((prefix) =>
      resolved.pathname.startsWith(prefix)
    );
    if (!hasTilesPrefix) {
      return false;
    }

    const allowedHosts = new Set<string>();
    for (const candidate of [
      fallbackOrigin,
      getTileserverUrl(),
      getPgTileserverUrl(),
      getApiUrl(),
    ]) {
      if (!candidate) continue;
      try {
        const hostname = new URL(
          getApiOriginFromBase(candidate, fallbackOrigin),
          fallbackOrigin
        ).hostname;
        if (hostname) {
          allowedHosts.add(hostname);
        }
      } catch {
        // ignore invalid URLs
      }
    }

    return allowedHosts.has(resolved.hostname);
  } catch {
    return TILES_PATH_PREFIXES.some((prefix) => rawUrl.startsWith(prefix));
  }
};
